/* ── ForexMind API Adapter ──
 *  前端统一 API 层，所有数据请求经此 adapter 发出
 *  前端页面只通过此 adapter 消费 App API，不直接维护业务事实。
 *  API 命名规范：
 *    follower-accounts     — MT4 交易账户
 *    platform-agents       — 平台已上架 AI 智能体
 *    follow-relations      — 跟单关系
 *    ai-decisions          — AI 决策记录
 *  动作接口：:submit :release :pause :resume :stop
 */

import { apiRequest } from '@/services/appApi';

export interface MarketProjectionContext {
  broker_catalog_id: 'icmarkets' | 'gtc' | 'mega';
  platform: 'mt4' | 'mt5';
  stream_id: string;
  source_revision: number;
  route_revision: number;
}
export interface MarketRequestScope { accountId: string; agentId?: string }
export function sameMarketSource(a?: MarketProjectionContext | null, b?: MarketProjectionContext | null): boolean {
  return !!a && !!b && a.broker_catalog_id === b.broker_catalog_id && a.platform === b.platform
    && a.stream_id === b.stream_id && a.source_revision === b.source_revision && a.route_revision === b.route_revision;
}
function marketQuery(scope: MarketRequestScope): URLSearchParams {
  const query = new URLSearchParams({ account_id: scope.accountId });
  if (scope.agentId) query.set('agent_id', scope.agentId);
  return query;
}
function verifiedMarketRows(items: MarketTickerDTO[]): MarketTickerDTO[] {
  if (items.some(item => !item.market_context)) throw new Error('行情来源尚未核验');
  return items;
}

export interface SymbolSupportDTO {
  schema_version: 'SymbolSupportV1';
  status: 'ready' | 'pending' | 'stale' | 'mismatch' | 'unavailable';
  configured_symbols: string[];
  supported_symbols: string[];
  unresolved_symbols: string[];
  observed_at: number | null;
  reason_code: string | null;
}
export interface SymbolCompatibilityDTO {
  eligible: boolean;
  required_symbols: string[];
  missing_symbols: string[];
  message: string;
}

type AccountStatus = 'reviewing' | 'provisioning' | 'failed' | 'status_unknown' | 'connected' | 'released';

type FollowStatus =
  | 'not_started'
  | 'configuring'
  | 'pending_risk_confirm'
  | 'running'
  | 'paused_by_user'
  | 'paused_by_platform'
  | 'agent_unavailable'
  | 'status_unknown'
  | 'stopped';

interface BrokerAccount {
  symbolSupport?: SymbolSupportDTO;
  symbolCompatibility?: SymbolCompatibilityDTO;
  accountEnvironment?: 'simulation' | 'real' | 'unknown';
  exitOnly?: boolean;
  executionReadiness?: {
    state: 'ready' | 'not_ready' | 'exit_only' | 'unknown';
    effective_for_new_opens: boolean;
    reason_codes: string[];
    observed_at: number | null;
  };
  onboarding?: { stage: string; stage_label: string; submission_revision?: number };
  usageStatus?: 'available' | 'following' | 'occupied';
  id: string;
  type: 'mt4' | 'mt5' | 'api';
  brokerName: string;
  accountNo: string;
  server: string;
  balance: string;
  margin: string;
  accountStatus: AccountStatus;
  connectedAt: string;
  login?: string;
  password?: string;
}

interface FollowRecord {
  id: string;
  agentId: number | string;
  accountId: string;
  accountNo: string;
  server: string;
  brokerName: string;
  followStatus: FollowStatus;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// ═══════════════════════════════════════
//  API 端点映射（后续替换为真实 endpoint）
// ═══════════════════════════════════════
const API = {
  // 交易账户
  followerAccounts: {
    list: '/app/follower-accounts',
    submit: '/app/follower-accounts/submit',
    release: '/app/follower-accounts/:id/release',
  },
  // 平台智能体
  platformAgents: {
    list: '/app/platform-agents',
    detail: '/app/platform-agents/:id',
  },
  // 跟单关系
  followRelations: {
    list: '/app/follow-relations',
    create: '/app/follow-relations/submit',
    pause: '/app/follow-relations/:id/pause',
    resume: '/app/follow-relations/:id/resume',
    stop: '/app/follow-relations/:id/stop',
  },
  // AI 决策
  aiDecisions: {
    list: '/app/ai-decisions',
    detail: '/app/ai-decisions/:id',
  },
  positions: {
    list: '/app/positions',
  },
  market: {
    tickers: '/app/market/tickers',
    tickerDetail: '/app/market/tickers/:symbol',
    tickerHistory: '/app/market/tickers/:symbol/history',
  },
  followPerformance: {
    summary: '/app/follow-performance/summary',
  },
  notifications: {
    list: '/app/notifications',
  },
} as const;

// ═══════════════════════════════════════
//  FollowerAccount 字段规范
//  account_status: reviewing | connected | released
//  usage_status: available | following | occupied
//  current_agent_id: number | null
//  internal_ea_status: connected | disconnected | error
//  internal_terminal: online | offline
// ═══════════════════════════════════════

export interface FollowerAccountDTO {
  platform?: 'mt4' | 'mt5';
  symbol_support?: SymbolSupportDTO;
  symbol_compatibility?: SymbolCompatibilityDTO;
  account_environment?: 'simulation' | 'real' | 'unknown';
  exit_only?: boolean;
  account_execution_readiness?: {
    state: 'ready' | 'not_ready' | 'exit_only' | 'unknown';
    effective_for_new_opens: boolean;
    reason_codes: string[];
    observed_at: number | null;
  };
  id: string;
  follower_account_id?: string;
  broker_name: string;
  broker?: string;
  account_no: string;
  mt4_login_masked?: string;
  server: string;
  account_status: AccountStatus;
  submission_revision?: number;
  review_status?: string;
  onboarding?: { stage: string; stage_label: string; submission_revision?: number };
  usage_status: 'available' | 'following' | 'occupied';
  current_agent_id: number | null;
  created_at: string;
  // 仅 B 端可见
  internal_ea_status?: string;
  internal_terminal?: string;
}

export interface PlatformAgentDTO {
  id: string;
  agent_id: string;
  name: string;
  strategy_type: string;
  supported_symbols: string[];
  strategy_timeframes?: { symbol: string; timeframe: 'M5' | 'M15' | 'M30' | 'H1' }[];
  risk_level: string;
  total_return: number | null;
  win_rate: number | null;
  max_drawdown: number | null;
  follower_count: number | null;
  sharpe_ratio?: number;
  running_time?: string;
  status: 'available' | 'following' | 'unavailable' | 'validating';
  validation_binding_id?: string;
  eligible_follower_account_ids?: string[];
  is_validation?: boolean;
}

export interface ReviewedStrategyDTO {
  config_id: string;
  config_version: number;
  config_hash: string;
  strategy_ref: { id: string; version: number; content_hash: string };
  risk_hash: string;
  risk_summary: {
    currency: string;
    sizing_mode: 'fixed_lot';
    fixed_lot: string;
    max_lot_per_order: string;
    max_total_lots: string;
    max_open_positions: number;
    max_daily_loss: string;
    min_free_margin: string;
  };
}

export interface SubscriptionAuthorizationDTO {
  relation_id: string;
  reviewed_strategy: ReviewedStrategyDTO | null;
  readiness: 'not_configured' | 'needs_reauthorization' | 'paused' | 'ready';
  effective_for_new_opens: boolean;
  account_effective_risk?: {
    status: 'available' | 'unavailable';
    currency?: string;
    limits?: { max_daily_loss: string; max_total_lots: string; max_open_positions: number; min_free_margin: string };
    sources?: { agent_name: string | null; agent_lifecycle: string | null; strategy_version: number; max_daily_loss: string }[];
    reason_code?: string;
  };
  consented_strategy_ref?: ReviewedStrategyDTO['strategy_ref'];
  consented_risk_hash?: string;
  state?: 'active' | 'paused' | 'revoked';
  revision?: number;
  expires_at?: number | null;
  validation_binding_id?: string;
}

export interface SubscriptionConsentPut {
  expected_revision: number;
  consented_strategy_ref: ReviewedStrategyDTO['strategy_ref'];
  consented_risk_hash: string;
  state: 'active' | 'paused' | 'revoked';
  expires_at?: null;
  validation_binding_id?: string;
}

export interface FollowRelationDTO {
  id: string;
  execution_schema?: string;
  agent_id: number | string;
  account_id: string;
  account_no: string;
  server: string;
  broker_name: string;
  follow_status: string;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface AiDecisionDTO {
  signal_id?: string | null;
  record_type?: 'mtf_run' | 'legacy_decision';
  summary?: string;
  frequency_label?: string;
  follower_execution?: {status: string; execution_id: string | null};
  analysis_outcome?: string;
  ai_call_status?: string;
  public_reason?: string;
  reason_code?: string;
  reasons?: Array<{kind: string; summary: string}>;
  analysis_basis?: Array<{kind: string; label: string; summary: string}>;
  data_as_of?: string;
  updated_at?: string;
  evidence_status?: string;
  stages?: Array<{stage: string; label: string; status: string; reason?: string | null; occurred_at?: string | null}>;
  strategy_trade?: {signal_status: string; master_execution_status: string};
  my_accounts?: Array<{
    follower_account_id: string;
    account_label: string;
    follow_relation_id: string;
    participation_status: string;
    execution_status: string;
    reason?: string | null;
    execution_id?: string | null;
    updated_at?: string | null;
  }>;
  id: string;
  decision_id: string;
  agent_id: string;
  follow_relation_id: string;
  symbol: string;
  action: string | null;
  confidence: number | null;
  status: string;
  created_at: string;
}

export interface OwnSignalExecutionTargetDTO {
  target_id: string;
  account_scope: string;
  target_state: string;
  readiness_reason: string | null;
  command_id: string | null;
  command_status: string | null;
  result_phase: string | null;
  order_effect: string | null;
  result: Record<string, unknown> | null;
  created_at: number;
  updated_at: number;
}

export interface TradePositionDTO {
  id: string;
  follower_account_id: string;
  follower_account_masked?: string;
  broker_ticket_masked: string;
  symbol: string;
  action: string;
  lot: number | null;
  open_price: number | null;
  current_price: number | null;
  pnl: number | null;
  pnl_currency?: string | null;
  lifecycle_status: 'open' | 'closed';
  close_source?: 'user_position_close' | null;
  close_request_id?: string | null;
  sync_status?: 'fresh' | 'stale' | 'error' | 'unverified';
  protection_status?: string | null;
  time_status?: string | null;
  closed_at?: string | null;
  pnl_as_of?: number | null;
  opened_at: string;
  updated_at: string;
}

export interface PositionClosePreviewDTO {
  position_id: string;
  account: { id: string; masked: string; platform: 'mt4' | 'mt5'; accounting_mode: 'hedging' | 'netting' | null };
  symbol: string;
  side: 'BUY' | 'SELL';
  position_masked: string;
  remaining_lot: string | null;
  position_revision: number | null;
  position_snapshot_hash: string | null;
  as_of: number | null;
  can_close: boolean;
  blocking_reasons: Array<{ code: string; message: string }>;
  active_request: { request_id: string; status: string; revision: number } | null;
  close_mode: 'full';
  reference_pnl: { amount: string | null; currency: string | null; as_of: number | null; quality: string };
  follow_after_close: 'unchanged';
  follow_state: 'running' | 'paused' | 'stopped' | 'unknown';
  subscription_state: 'active' | 'paused' | 'revoked' | 'unknown';
  follow_notice: string;
  consent_version: 'user-position-close-v1';
}

export interface PositionCloseRequestDTO {
  request_id: string;
  position_id: string;
  status: string;
  terminal: boolean;
  reason_code: string | null;
  message: string;
  requested_close_lot: string;
  confirmed_closed_lot: string | null;
  confirmed_remaining_lot: string | null;
  reported_closed_lot: string | null;
  broker_finality: string;
  execution_stage: string;
  command_id: string | null;
  projection_status: string;
  pnl: { amount: string; currency: string; quality: 'complete'; as_of: number } | null;
  can_reconfirm_remaining: boolean;
  created_at: number;
  updated_at: number;
  revision: number;
}

export function executionStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    queued: '等待发送', submitted: '等待执行结果', sent: '等待执行结果',
    accepted: 'EA 已接受 · 等待成交', filled: 'EA 报告成交 · 待核对',
    open: 'Broker 持仓已核对',
    closed: '已平仓', rejected: '已拒绝', failed: '执行失败',
    expired: '已过期', skipped: '未执行', unknown: '结果待核对',
    quarantined: '已隔离 · 未发送',
    claimed: '正在发送', dispatched_to_ea: '已发送 · 等待回报',
    result_unknown: '执行结果待核对', dead_letter: '已停止发送',
  };
  return labels[status] || '状态待核对';
}

export function verifiedPnlText(value: number | null | undefined, currency: string | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || !currency) return '盈亏待核对';
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)} ${currency}`;
}

export interface FollowPerformanceSummaryDTO {
  active_follow_relations: number;
  total_follow_relations: number;
  total_pnl: number | null;
  pnl_currency: string | null;
  closed_trade_count: number;
  unverified_closed_count: number;
  win_rate: number | null;
  max_drawdown: number | null;
  max_single_loss: number | null;
  curve: number[];
  by_symbol: Array<{symbol: string; pnl: number; trades: number; wins: number}>;
  period_days: number;
  execution_count: number;
  max_recent_order_query_days: number;
}

export interface NotificationSummaryDTO {
  total: number;
  unread: number;
}

type FollowerAccountWire = {
  platform?: 'mt4' | 'mt5';
  symbol_support?: SymbolSupportDTO;
  symbol_compatibility?: SymbolCompatibilityDTO;
  account_environment?: FollowerAccountDTO['account_environment'];
  exit_only?: boolean;
  account_execution_readiness?: FollowerAccountDTO['account_execution_readiness'];
  follower_account_id: string;
  broker: string;
  server: string;
  mt4_login_masked: string;
  account_status: 'submitted' | 'reviewing' | 'linked' | 'rejected' | 'released' | 'need_material' | string;
  usage_status: 'not_available' | 'available' | 'in_use' | 'released' | string;
  submission_revision?: number;
  review_status?: string;
  onboarding?: { stage: string; stage_label: string; submission_revision?: number };
  created_at: number | string;
};

type PageResponse<T> = {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
  next_cursor?: string | null;
};

type FollowRelationWire = {
  follow_relation_id: string;
  execution_schema?: string;
  agent_id: string;
  follower_account_id: string;
  symbols?: string[];
  follow_status: string;
  created_at: number | string;
  updated_at: number | string;
};

type PlatformAgentWire = {
  agent_id: string;
  name: string;
  status: string;
  supported_symbols: string[];
  strategy_timeframes?: PlatformAgentDTO['strategy_timeframes'];
  strategy_type: string;
  performance_summary?: {
    win_rate?: number | null;
    max_drawdown_pct?: number | null;
    followers?: number | null;
    total_return?: number | null;
  };
  risk_summary?: {
    risk_level?: string;
  };
  validation_binding_id?: string;
  eligible_follower_account_ids?: string[];
  is_validation?: boolean;
};

type AiDecisionWire = {
  signal_id?: string | null;
  record_type?: 'mtf_run' | 'legacy_decision';
  summary?: string;
  frequency_label?: string;
  follower_execution?: {status: string; execution_id: string | null};
  analysis_outcome?: string;
  ai_call_status?: string;
  public_reason?: string;
  reason_code?: string;
  reasons?: Array<{kind: string; summary: string}>;
  data_as_of?: number | string;
  updated_at?: number | string;
  evidence_status?: string;
  stages?: Array<{stage: string; label: string; status: string; reason?: string | null; occurred_at?: number | string | null}>;
  strategy_trade?: {signal_status: string; master_execution_status: string};
  my_accounts?: Array<{
    follower_account_id: string;
    account_label: string;
    follow_relation_id: string;
    participation_status: string;
    execution_status: string;
    reason?: string | null;
    execution_id?: string | null;
    updated_at?: number | string | null;
  }>;
  decision_id: string;
  agent_id: string;
  follow_relation_id: string;
  symbol: string;
  action: string | null;
  confidence?: number;
  status?: string;
  decision_status?: string;
  created_at: number | string;
};

type TradePositionWire = {
  projection_id: string;
  follower_account_id: string;
  follower_account_masked?: string;
  broker_ticket_masked: string;
  symbol: string;
  action: string;
  lot?: string;
  open_price?: string;
  current_price?: string;
  pnl?: string;
  pnl_currency?: string | null;
  lifecycle_status: 'open' | 'closed';
  close_source?: 'user_position_close' | null;
  close_request_id?: string | null;
  sync_status?: 'fresh' | 'stale' | 'error' | 'unverified';
  protection_status?: string | null;
  time_status?: string | null;
  closed_at?: number | string | null;
  pnl_as_of?: number | null;
  opened_at: number | string;
  opened_at_utc?: number | null;
  updated_at: number | string;
};

type NotificationWire = {
  notification_id: string;
  read: boolean;
};

export interface MarketTickerDTO {
  market_context?: MarketProjectionContext | null;
  symbol: string;
  bid: string | number;
  ask: string | number;
  mid?: string | number | null;
  change_pips: number;
  change_unit_label?: string | null;
  change_pct: number;
  spread_points: number;
  spread_units?: string | null;
  spread_unit_label?: string | null;
  digits?: number | null;
  status: 'open' | 'closed' | string;
  agent_count?: number;
  position_count?: number;
  signal_status?: 'BUY' | 'SELL' | 'HOLD' | 'none' | 'protected';
  terminal_id?: string;
  source_id?: string;
  quote_time?: number | null;
  quote_status?: string;
  freshness_status?: string;
}

export type MarketTickerTimeframe = '1H' | '4H' | '1D' | '1W';

export interface MarketTickerHistoryItemDTO {
  timestamp: number;
  bid: string | number;
  ask: string | number;
  mid: string | number;
  spread_points: number;
}

export interface MarketTickerHistoryDTO {
  market_context?: MarketProjectionContext | null;
  symbol: string;
  timeframe: MarketTickerTimeframe;
  items: MarketTickerHistoryItemDTO[];
}

export const APP_AGENT_API_IDS: Record<number, string> = {
  1: 'fxa_momentum',
  2: 'fxa_gold',
  3: 'fxa_momentum',
};

const API_AGENT_UI_IDS: Record<string, number> = Object.entries(APP_AGENT_API_IDS).reduce<Record<string, number>>(
  (acc, [uiId, apiId]) => {
    if (acc[apiId] === undefined) acc[apiId] = Number(uiId);
    return acc;
  },
  {},
);

export function toApiAgentId(agentId: number | string): string {
  if (typeof agentId === 'number') return APP_AGENT_API_IDS[agentId] || String(agentId);
  if (/^\d+$/.test(agentId)) return APP_AGENT_API_IDS[Number(agentId)] || agentId;
  return agentId;
}

export function toUiAgentId(agentId: number | string): number | string {
  if (typeof agentId === 'number') return agentId;
  if (/^\d+$/.test(agentId)) return Number(agentId);
  return API_AGENT_UI_IDS[agentId] ?? agentId;
}

function toDateTimeText(value: number | string): string {
  if (typeof value === 'number') {
    const date = new Date(value * 1000);
    return value > 946684800 && !Number.isNaN(date.getTime()) ? date.toISOString() : '时间待核对';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
}

function mapAccountStatus(account: FollowerAccountWire): FollowerAccountDTO['account_status'] {
  if (account.review_status === 'reviewing') return 'reviewing';
  if (account.onboarding?.stage === 'awaiting_review') return 'reviewing';
  if (account.onboarding?.stage === 'failed') return 'failed';
  if (account.onboarding?.stage === 'status_unknown') return 'status_unknown';
  if (account.account_status === 'linked') return 'connected';
  if (account.account_status === 'released' || account.usage_status === 'released' || account.account_status === 'rejected') return 'released';
  if (account.review_status === 'approved' || account.review_status === 'ea_provisioning' || account.account_status === 'waiting_ea') return 'provisioning';
  return 'reviewing';
}

function mapUsageStatus(account: FollowerAccountWire): FollowerAccountDTO['usage_status'] {
  if (account.usage_status === 'exit_pending') return 'occupied';
  if (account.usage_status === 'in_use') return 'following';
  if (account.account_status === 'linked') return 'available';
  return 'occupied';
}

export function toBrokerAccount(account: FollowerAccountDTO): BrokerAccount {
  return {
    id: account.id,
    symbolSupport: account.symbol_support,
    symbolCompatibility: account.symbol_compatibility,
    accountEnvironment: account.account_environment,
    exitOnly: account.exit_only,
    executionReadiness: account.account_execution_readiness,
    type: account.platform ?? 'mt4',
    brokerName: account.broker_name,
    accountNo: account.account_no,
    server: account.server,
    // The follower-account projection does not provide verified monetary values.
    balance: '—',
    margin: '—',
    accountStatus: account.account_status,
    onboarding: account.onboarding || (account.review_status === 'reviewing' ? { stage: 'awaiting_review', stage_label: '待审核', submission_revision: account.submission_revision } : undefined),
    usageStatus:account.usage_status,
    connectedAt: account.created_at,
  };
}

function mapFollowerAccount(account: FollowerAccountWire): FollowerAccountDTO {
  return {
    id: account.follower_account_id,
    platform: account.platform,
    symbol_support: account.symbol_support,
    symbol_compatibility: account.symbol_compatibility,
    account_environment: account.account_environment,
    exit_only: account.exit_only,
    account_execution_readiness: account.account_execution_readiness,
    follower_account_id: account.follower_account_id,
    broker_name: account.broker,
    broker: account.broker,
    account_no: account.mt4_login_masked,
    mt4_login_masked: account.mt4_login_masked,
    server: account.server,
    account_status: mapAccountStatus(account),
    review_status: account.review_status,
    submission_revision: account.submission_revision,
    onboarding: account.onboarding,
    usage_status: mapUsageStatus(account),
    current_agent_id: null,
    created_at: toDateTimeText(account.created_at),
  };
}

function mapFollowRelation(relation: FollowRelationWire): FollowRelationDTO {
  const symbols = relation.symbols || [];
  return {
    id: relation.follow_relation_id,
    execution_schema: relation.execution_schema,
    agent_id: relation.agent_id,
    account_id: relation.follower_account_id,
    account_no: relation.follower_account_id,
    server: '',
    broker_name: '',
    follow_status: relation.follow_status,
    config: {
      allowedPairs: symbols,
    },
    created_at: toDateTimeText(relation.created_at),
    updated_at: toDateTimeText(relation.updated_at),
  };
}

function mapPlatformAgent(agent: PlatformAgentWire): PlatformAgentDTO {
  return {
    id: agent.agent_id,
    agent_id: agent.agent_id,
    name: agent.name,
    strategy_type: agent.strategy_type,
    supported_symbols: agent.supported_symbols || [],
    strategy_timeframes: agent.strategy_timeframes,
    risk_level: agent.risk_summary?.risk_level || 'unknown',
    total_return: agent.performance_summary?.total_return ?? null,
    win_rate: agent.performance_summary?.win_rate ?? null,
    max_drawdown: agent.performance_summary?.max_drawdown_pct ?? null,
    follower_count: agent.performance_summary?.followers ?? null,
    status: agent.status === 'listed' ? 'available' : agent.status === 'validating' ? 'validating' : 'unavailable',
    validation_binding_id: agent.validation_binding_id,
    eligible_follower_account_ids: agent.eligible_follower_account_ids,
    is_validation: agent.is_validation,
  };
}

function mapFollowStatus(status: string): FollowStatus {
  if (status === 'paused') return 'paused_by_user';
  if (status === 'blocked') return 'agent_unavailable';
  if (['not_started', 'configuring', 'pending_risk_confirm', 'running', 'paused_by_user', 'paused_by_platform', 'agent_unavailable', 'stopped'].includes(status)) return status as FollowStatus;
  return 'status_unknown';
}

function mapAiDecision(decision: AiDecisionWire): AiDecisionDTO {
  return {
    id: decision.decision_id,
    decision_id: decision.decision_id,
    agent_id: decision.agent_id,
    follow_relation_id: decision.follow_relation_id,
    symbol: decision.symbol,
    action: decision.action,
    confidence: decision.confidence ?? null,
    record_type: decision.record_type,
    summary: decision.summary,
    frequency_label: decision.frequency_label,
    follower_execution: decision.follower_execution,
    analysis_outcome: decision.analysis_outcome,
    ai_call_status: decision.ai_call_status,
    public_reason: decision.public_reason,
    reason_code: decision.reason_code,
    reasons: decision.reasons,
    data_as_of: decision.data_as_of == null ? undefined : toDateTimeText(decision.data_as_of),
    updated_at: decision.updated_at === undefined ? undefined : toDateTimeText(decision.updated_at),
    evidence_status: decision.evidence_status,
    stages: decision.stages?.map((stage) => ({
      stage: stage.stage,
      label: stage.label,
      status: stage.status,
      reason: stage.reason,
      occurred_at: typeof stage.occurred_at === 'number' || typeof stage.occurred_at === 'string'
        ? toDateTimeText(stage.occurred_at)
        : undefined,
    })),
    strategy_trade: decision.strategy_trade,
    signal_id: decision.signal_id,
    my_accounts: decision.my_accounts?.map((account) => ({
      follower_account_id: account.follower_account_id,
      account_label: account.account_label,
      follow_relation_id: account.follow_relation_id,
      participation_status: account.participation_status,
      execution_status: account.execution_status,
      reason: account.reason,
      execution_id: account.execution_id,
      updated_at: typeof account.updated_at === 'number' || typeof account.updated_at === 'string'
        ? toDateTimeText(account.updated_at)
        : undefined,
    })),
    status: decision.status || decision.decision_status || 'created',
    created_at: toDateTimeText(decision.created_at),
  };
}

function mapTradePosition(position: TradePositionWire): TradePositionDTO {
  const optionalNumber = (value?: string | null): number | null => value == null || value === '' ? null : Number(value);
  return {
    id: position.projection_id,
    follower_account_id: position.follower_account_id,
    follower_account_masked: position.follower_account_masked,
    broker_ticket_masked: position.broker_ticket_masked,
    symbol: position.symbol,
    action: position.action,
    lot: optionalNumber(position.lot),
    open_price: optionalNumber(position.open_price),
    current_price: optionalNumber(position.current_price),
    pnl: optionalNumber(position.pnl),
    pnl_currency: position.pnl_currency ?? null,
    lifecycle_status: position.lifecycle_status,
    close_source: position.close_source ?? null,
    close_request_id: position.close_request_id ?? null,
    sync_status: position.sync_status,
    protection_status: position.protection_status,
    time_status: position.time_status,
    closed_at: position.closed_at ? toDateTimeText(position.closed_at) : null,
    pnl_as_of: position.pnl_as_of,
    opened_at: position.opened_at_utc ? toDateTimeText(position.opened_at_utc) : '',
    updated_at: toDateTimeText(position.updated_at),
  };
}

export function toFollowRecord(relation: FollowRelationDTO, accounts: BrokerAccount[] = []): FollowRecord {
  const account = accounts.find((item) => item.id === relation.account_id);
  return {
    id: relation.id,
    agentId: toUiAgentId(relation.agent_id),
    accountId: relation.account_id,
    accountNo: account?.accountNo || relation.account_no || relation.account_id,
    server: account?.server || relation.server || '',
    brokerName: account?.brokerName || relation.broker_name || '',
    followStatus: mapFollowStatus(relation.follow_status),
    config: relation.config,
    createdAt: relation.created_at,
    updatedAt: relation.updated_at,
  };
}

// ═══════════════════════════════════════
//  ApiService — 后续替换实现即可
// ═══════════════════════════════════════
export const forexApi = {
  // ── 平台智能体 ──
  async listPlatformAgents(accessToken?: string | null, signal?: AbortSignal): Promise<PlatformAgentDTO[]> {
    const data = await apiRequest<PageResponse<PlatformAgentWire>>(API.platformAgents.list, { method: 'GET', signal }, accessToken);
    return data.items.map(mapPlatformAgent);
  },

  async getPlatformAgent(agentId: number | string, accessToken?: string | null): Promise<PlatformAgentDTO> {
    const data = await apiRequest<PlatformAgentWire>(API.platformAgents.detail.replace(':id', toApiAgentId(agentId)), { method: 'GET' }, accessToken);
    return mapPlatformAgent(data);
  },

  async listValidationAgents(accessToken?: string | null, signal?: AbortSignal): Promise<PlatformAgentDTO[]> {
    const data = await apiRequest<PageResponse<PlatformAgentWire>>('/app/validation-agents',
      { method: 'GET', signal }, accessToken);
    return data.items.map(mapPlatformAgent);
  },

  async getValidationAgent(agentId: number | string, accessToken?: string | null): Promise<PlatformAgentDTO> {
    const data = await apiRequest<PlatformAgentWire>(
      `/app/validation-agents/${encodeURIComponent(toApiAgentId(agentId))}`,
      { method: 'GET' }, accessToken);
    return mapPlatformAgent(data);
  },

  async onboardingOptions(accessToken?: string | null): Promise<OnboardingOptions> {
    return apiRequest<OnboardingOptions>('/app/ea-onboarding/options', { method: 'GET' }, accessToken);
  },
  async onboardingAction(accountId: string, action: 'retry' | 'cancel', revision: number, accessToken?: string | null): Promise<unknown> {
    return apiRequest(`/app/follower-accounts/${accountId}/provisioning/${action}`, { method: 'POST', headers: {'Idempotency-Key':crypto.randomUUID()}, body: JSON.stringify({expected_revision:revision,reason_code:null}) }, accessToken);
  },
  async onboardingProgress(accountId: string, accessToken?: string | null, signal?: AbortSignal): Promise<OnboardingSummary | null> {
    return apiRequest<OnboardingSummary | null>(`/app/follower-accounts/${accountId}/provisioning`, { method: 'GET', signal }, accessToken);
  },

  // ── 交易账户 ──
  async listFollowerAccounts(accessToken?: string | null, signal?: AbortSignal, agentId?: string, validation = false): Promise<FollowerAccountDTO[]> {
    const data = await apiRequest<PageResponse<FollowerAccountWire>>(API.followerAccounts.list + (agentId ? `?agent_id=${encodeURIComponent(agentId)}&validation=${validation}` : ''), { method: 'GET', signal }, accessToken);
    return data.items.map(mapFollowerAccount);
  },

  async submitFollowerAccount(account: BrokerAccount, accessToken?: string | null, managed?: { consentVersion: string; operationKey: string; expectedResubmissionRevision?: string }): Promise<FollowerAccountDTO> {
    const data = await apiRequest<FollowerAccountWire>(API.followerAccounts.submit, {
      method: 'POST',
      headers: managed ? { 'Idempotency-Key': managed.operationKey } : undefined,
      body: JSON.stringify({
        platform: account.type,
        broker: account.brokerName,
        mt4_login: account.login || account.accountNo.replace(/\D/g, ''),
        mt4_password: account.password || '',
        server: account.server,
        ...(managed ? { provisioning_mode: 'managed_v1', credential_consent_version: managed.consentVersion } : {}),
      }),
    }, accessToken);
    return mapFollowerAccount(data);
  },

  async releaseFollowerAccount(accountId: string, accessToken?: string | null, reason = 'user requested'): Promise<FollowerAccountDTO> {
    const data = await apiRequest<FollowerAccountWire>(API.followerAccounts.release.replace(':id', accountId), {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }, accessToken);
    return mapFollowerAccount(data);
  },

  async resubmitFollowerAccount(accountId: string, account: BrokerAccount, accessToken?: string | null, managed?: { consentVersion: string; operationKey: string; expectedResubmissionRevision?: string }): Promise<FollowerAccountDTO> {
    const body: Record<string, string> = {
      platform: account.type,
      broker: account.brokerName,
      server: account.server,
    };
    if (managed) { body.provisioning_mode = 'managed_v1'; body.credential_consent_version = managed.consentVersion; }
    if (managed?.expectedResubmissionRevision) body.expected_resubmission_revision = managed.expectedResubmissionRevision;
    if (account.login) body.mt4_login = account.login;
    if (account.password) body.mt4_password = account.password;
    const data = await apiRequest<FollowerAccountWire>(`/app/follower-accounts/${accountId}/resubmit`, {
      method: 'POST',
      headers: managed ? { 'Idempotency-Key': managed.operationKey } : undefined,
      body: JSON.stringify(body),
    }, accessToken);
    return mapFollowerAccount(data);
  },

  // ── 跟单关系 ──
  async listFollowRelations(accessToken?: string | null, signal?: AbortSignal): Promise<FollowRelationDTO[]> {
    const data = await apiRequest<PageResponse<FollowRelationWire>>(API.followRelations.list, { method: 'GET', signal }, accessToken);
    return data.items.map(mapFollowRelation);
  },

  async getSubscriptionAuthorization(relationId: string, accessToken?: string | null): Promise<SubscriptionAuthorizationDTO> {
    return apiRequest<SubscriptionAuthorizationDTO>(`/app/follow-relations/${encodeURIComponent(relationId)}/subscription-authorization`, { method: 'GET' }, accessToken);
  },

  async getReviewedStrategy(agentId: string, accessToken?: string | null,
    validationBindingId?: string): Promise<ReviewedStrategyDTO> {
    const query = validationBindingId ? `?validation_binding_id=${encodeURIComponent(validationBindingId)}` : '';
    return apiRequest<ReviewedStrategyDTO>(`/app/agents/${encodeURIComponent(agentId)}/reviewed-strategy${query}`, { method: 'GET' }, accessToken);
  },

  async saveSubscriptionAuthorization(relationId: string, payload: SubscriptionConsentPut, idempotencyKey: string, accessToken?: string | null): Promise<SubscriptionAuthorizationDTO> {
    return apiRequest<SubscriptionAuthorizationDTO>(`/app/follow-relations/${encodeURIComponent(relationId)}/subscription-authorization`, {
      method: 'PUT', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(payload),
    }, accessToken);
  },

  async createFollowRelation(record: FollowRecord, accessToken?: string | null, idempotencyKey?: string,
    validationBindingId?: string): Promise<FollowRelationDTO> {
    const data = await apiRequest<FollowRelationWire>(API.followRelations.create, {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey || crypto.randomUUID() },
      body: JSON.stringify({
        agent_id: toApiAgentId(record.agentId),
        follower_account_id: record.accountId,
        ...(validationBindingId ? { validation_binding_id: validationBindingId } : {}),
        symbols: Array.isArray(record.config.allowedPairs) && record.config.allowedPairs.length > 0
          ? record.config.allowedPairs
          : ['EURUSD'],
      }),
    }, accessToken);
    return mapFollowRelation(data);
  },

  async pauseFollowRelation(relationId: string, accessToken?: string | null): Promise<FollowRelationDTO> {
    const data = await apiRequest<FollowRelationWire>(API.followRelations.pause.replace(':id', relationId), {
      method: 'POST',
      body: JSON.stringify({ reason: 'user requested' }),
    }, accessToken);
    return mapFollowRelation(data);
  },

  async resumeFollowRelation(relationId: string, accessToken?: string | null): Promise<FollowRelationDTO> {
    const data = await apiRequest<FollowRelationWire>(API.followRelations.resume.replace(':id', relationId), {
      method: 'POST',
    }, accessToken);
    return mapFollowRelation(data);
  },

  async stopFollowRelation(relationId: string, accessToken?: string | null): Promise<FollowRelationDTO> {
    const data = await apiRequest<FollowRelationWire>(API.followRelations.stop.replace(':id', relationId), {
      method: 'POST',
      body: JSON.stringify({ reason: 'user requested' }),
    }, accessToken);
    return mapFollowRelation(data);
  },

  async unbindFollowRelation(relationId: string, accessToken?: string | null): Promise<FollowRelationDTO> {
    return this.stopFollowRelation(relationId, accessToken);
  },

  // ── AI 决策 / 执行 / 表现 ──
  async listAiDecisions(accessToken?: string | null, page = 1, pageSize = 20, filters: { followedOnly?: boolean; dateFrom?: string } = {}, signal?: AbortSignal): Promise<PageResponse<AiDecisionDTO>> {
    const query = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (filters.followedOnly) query.set('followed_only', 'true');
    if (filters.dateFrom) query.set('date_from', filters.dateFrom);
    const data = await apiRequest<PageResponse<AiDecisionWire>>(
      `${API.aiDecisions.list}?${query}`,
      { method: 'GET', signal },
      accessToken,
    );
    return { ...data, items: data.items.map(mapAiDecision) };
  },

  async getAiDecision(id: string, accessToken?: string | null): Promise<AiDecisionDTO> {
    const data = await apiRequest<AiDecisionWire>(API.aiDecisions.detail.replace(':id', encodeURIComponent(id)), {method: 'GET'}, accessToken);
    return mapAiDecision(data);
  },

  async listOwnSignalExecution(signalId: string, accessToken?: string | null, cursor?: string | null): Promise<{signal_id: string; items: OwnSignalExecutionTargetDTO[]; next_cursor: string | null}> {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return apiRequest(`/app/strategy-signals/${encodeURIComponent(signalId)}/execution${query}`, { method: 'GET' }, accessToken);
  },

  async listPositions(accessToken?: string | null, status: 'open' | 'closed' = 'open'): Promise<TradePositionDTO[]> {
    const data = await apiRequest<PageResponse<TradePositionWire>>(
      `${API.positions.list}?status=${status}&page=1&page_size=100`,
      { method: 'GET' },
      accessToken,
    );
    return data.items.map(mapTradePosition);
  },

  async previewPositionClose(positionId: string, accessToken?: string | null): Promise<PositionClosePreviewDTO> {
    return apiRequest<PositionClosePreviewDTO>(
      `/app/positions/${encodeURIComponent(positionId)}/close-preview`,
      { method: 'GET' }, accessToken,
    );
  },

  async submitPositionClose(positionId: string, payload: {
    request_id: string; mode: 'full'; expected_position_revision: number;
    expected_remaining_lot: string; position_snapshot_hash: string;
    consent_version: 'user-position-close-v1';
  }, accessToken?: string | null): Promise<PositionCloseRequestDTO> {
    return apiRequest<PositionCloseRequestDTO>(
      `/app/positions/${encodeURIComponent(positionId)}/close-requests`,
      { method: 'POST', headers: { 'Idempotency-Key': payload.request_id },
        body: JSON.stringify(payload) }, accessToken,
    );
  },

  async getPositionCloseRequest(requestId: string, accessToken?: string | null): Promise<PositionCloseRequestDTO> {
    return apiRequest<PositionCloseRequestDTO>(
      `/app/position-close-requests/${encodeURIComponent(requestId)}`,
      { method: 'GET' }, accessToken,
    );
  },

  async listClosedTradePositions(accessToken?: string | null, cursor?: string | null, symbol?: string): Promise<{items: TradePositionDTO[]; next_cursor: string | null}> {
    const query = new URLSearchParams({ status: 'closed', page_size: '50' });
    if (cursor) query.set('cursor', cursor);
    if (symbol) query.set('symbol', symbol);
    const page = await apiRequest<PageResponse<TradePositionWire>>(`${API.positions.list}?${query}`, { method: 'GET' }, accessToken);
    return { items: page.items.map(mapTradePosition), next_cursor: page.next_cursor ?? null };
  },

  async getFollowPerformanceSummary(accessToken?: string | null, days = 180): Promise<FollowPerformanceSummaryDTO> {
    return apiRequest<FollowPerformanceSummaryDTO>(`${API.followPerformance.summary}?days=${days}`, { method: 'GET' }, accessToken);
  },

  async listMarketTickers(accessToken?: string | null, sort = 'symbol_asc', scope?: MarketRequestScope, signal?: AbortSignal): Promise<MarketTickerDTO[]> {
    if (!scope) return [];
    const query = marketQuery(scope);
    query.set('sort', sort); query.set('page', '1'); query.set('page_size', '50');
    const data = await apiRequest<PageResponse<MarketTickerDTO>>(
      `${API.market.tickers}?${query}`, { method: 'GET', signal }, accessToken,
    );
    return verifiedMarketRows(data.items);
  },

  async listMarketQuotes(accessToken?: string | null, sort = 'symbol_asc', scope?: MarketRequestScope, signal?: AbortSignal): Promise<MarketTickerDTO[]> {
    return this.listMarketTickers(accessToken, sort, scope, signal);
  },

  async getMarketTicker(symbol: string, accessToken: string | null | undefined, scope: MarketRequestScope, signal?: AbortSignal): Promise<MarketTickerDTO> {
    const item = await apiRequest<MarketTickerDTO>(
      `${API.market.tickerDetail.replace(':symbol', encodeURIComponent(symbol))}?${marketQuery(scope)}`,
      { method: 'GET', signal }, accessToken,
    );
    return verifiedMarketRows([item])[0];
  },

  async getMarketTickerHistory(symbol: string, timeframe: MarketTickerTimeframe, accessToken?: string | null,
    scope?: MarketRequestScope, source?: MarketProjectionContext | null, signal?: AbortSignal): Promise<MarketTickerHistoryDTO> {
    if (!scope || !source) return { symbol, timeframe, items: [] };
    const path = API.market.tickerHistory.replace(':symbol', encodeURIComponent(symbol));
    const query = marketQuery(scope);
    query.set('timeframe', timeframe); query.set('market_stream_id', source.stream_id);
    query.set('market_route_revision', String(source.route_revision));
    const data = await apiRequest<MarketTickerHistoryDTO>(`${path}?${query}`, { method: 'GET', signal }, accessToken);
    if (data.items.length && !sameMarketSource(data.market_context, source)) throw new Error('行情来源已变化，请刷新');
    return data;
  },

  async getNotificationSummary(accessToken?: string | null): Promise<NotificationSummaryDTO> {
    const data = await apiRequest<PageResponse<NotificationWire>>(
      `${API.notifications.list}?unread_only=true&page=1&page_size=1`,
      { method: 'GET' },
      accessToken,
    );
    return { total: data.total, unread: data.total };
  },
};

export default forexApi;

export interface OnboardingOptions {
  mode: 'managed_v1'; catalog_version: string; consent_version: string;
  servers: Array<{ broker: string; server: string; environment: string; readiness?: 'template_listed'; reason_code?: 'RUNTIME_ADMISSION_DECIDED_PER_JOB'; owner?: 'platform' }>;
  platform_servers?: Array<{ platform: 'mt4' | 'mt5'; broker: string; server: string; environment: string; readiness?: 'template_listed'; reason_code?: 'RUNTIME_ADMISSION_DECIDED_PER_JOB'; owner?: 'platform' }>;
}
export interface OnboardingSummary {
  can_resubmit?: boolean; resubmit_block_reason?: string | null; resubmission_revision?: string | null;
  submission_revision?: number; review_id?: string | null; review_revision?: number; provision_job_id?: string | null;
  execution_status_label?: string; reason_messages?: string[];
  mode: 'managed_v1'; revision?: number; stage: string; stage_label: string; updated_at: number;
  error_code: string | null; action_required: string | null;
  can_retry: boolean; can_cancel: boolean;
  connection_status: 'pending' | 'online' | 'offline'; admin_sync_status: string;
  diagnostics?: {
    stage: string; blocker_code: string | null;
    owner: 'account' | 'platform' | 'node' | 'operator' | 'none';
    next_action: 'wait' | 'resubmit_credentials' | 'retry' | 'contact_operator' | 'none';
    retryable: boolean; dependency_revision: number; updated_at: number;
  };
}
